<template>
  <forgerock-template id="set-password" :title="$t('dss.page.setPW.title')">

    <bootstrap-alerts :alerts="alerts"/>

    <stihl-section :title="email">
      <stihl-overlay :active="state === 'loading'" :spinner="true">

        <form @submit.prevent="save">

          <group-input id="newPass"
                       type="password"
                       :label="$t('dss.label.newPassword')"
                       group-class="form-group"
                       :required="true"
                       autocomplete="new-password"
                       autofocus
                       v-model="v$.form.newPassword.$model"
                       :validation-errors="v$.form.newPassword.$errors"
                       @keyup.enter="save"/>

          <group-input id="confirmPass"
                       type="password"
                       :label="$t('dss.label.newPassword2')"
                       group-class="form-group"
                       :required="true"
                       autocomplete="new-password"
                       v-model="v$.form.newPassword2.$model"
                       :validation-errors="v$.form.newPassword2.$errors"
                       @keyup.enter="save"/>

          <group-switch id="tcSwitch"
                        v-model="v$.form.terms.$model"
                        :validation-errors="v$.form.terms.$errors"
                        v-if="showTC"
                        :label="$t('dss.page.setPW.acceptTC', {
              terms:`<a href='${localizedLink(legal.terms_url)}'>${$t('dss.page.setPW.terms')}</a>`,
              privacy:`<a href='${localizedLink(legal.privacy_url)}'>${$t('dss.page.setPW.privacy')}</a>`,
            })"></group-switch>

          <group-switch id="killSSOSwitch"
                        v-model="form.killSSO"
                        :label="$t('dss.page.setPW.killSSO')"
                        v-if="!showTC"/>

          <div class="buttons">
            <button id="save" type="button" class="btn btn-primary" @click="save" :disabled="state==='loading'">
              {{ $t('dss.button.continue') }}
            </button>
            <help-menu/>
          </div>
        </form>

      </stihl-overlay>
    </stihl-section>

  </forgerock-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import GroupInput from '@/components/ui/GroupInput.vue';
import { Alert } from '@/components/ui/BootstrapAlerts.vue';
import useVuelidate from '@vuelidate/core';
import {
  hasDigit,
  hasLowercase,
  hasSpecial,
  hasUppercase,
  invalidCharacters,
  maxLength,
  minLength,
  required,
  sameAs,
  tcAccepted
} from '@/components//validators';
import ForgerockTemplate from '@/components/layout/ForgerockTemplate.vue';
import GroupSwitch from '@/components/ui/GroupSwitch.vue';
import HelpMenu from '@/components/ui/HelpMenu.vue';
import Backend, { RestError } from '@/components//backend';
import { mapState } from 'pinia';
import { useUserStore } from '@/components/user.store';
import { AxiosError } from 'axios';
import { localizedLink } from '@/lang';

export default defineComponent({
  name: 'SetPasswordPage',
  components: { HelpMenu, GroupSwitch, ForgerockTemplate, StihlSection, GroupInput },
  data() {
    return {
      form: {
        newPassword: '',
        newPassword2: '',
        terms: false,
        killSSO: !this.showTC, //
      },
      legal: window.APP_CONFIG.legal,
      state: 'ready' as 'ready' | 'loading' | 'error',
      alerts: [] as Alert[],
    };
  },
  computed: {
    ...mapState(useUserStore, ['email', 'setPasswordToken']),
    showTC() {
      return this.$route.query.showTC === 'true'; // show T&C to registration complete
    }
  },
  methods: {
    localizedLink,
    alert(message: string, _class?: string) {
      this.alerts.push({ message, class: _class });
    },

    save() {
      if (this.state === 'loading') {
        console.log('wait. loading');
        return;
      }

      (this.v$ as any).form.$touch(); // trigger validation
      if (this.v$.$invalid) return console.error('validation failed', this.v$.$errors);

      this.state = 'loading';
      this.alerts = [];

      Backend.setPassword({
        email: this.email as string,
        token: this.setPasswordToken as string,
        newPassword: this.form.newPassword,
        isTCAccepted: this.form.terms,
        invalidateSessions: this.form.killSSO,
      })
          .then(_r => {
            this.alert(this.$t('dss.page.security.msg.pwUpdated'), 'alert-info');
            this.state = 'ready';
            console.log('[pw-set]goto info');
            this.$router.push({ path: '/info/postPwSet' });
          })
          .catch((e: AxiosError<RestError>) => {
            this.state = 'error';
            if (e.response?.data?.code === 'INVALID_PARAMETER') {
              switch (e.response.data.invalidField) {
                case 'password':
                  return this.alert(this.$t('dss.page.security.msg.invalidPassword'));
                case 'token':
                  return this.alert(this.$t('dss.page.setPW.msg.tokenExpired', {
                    link: `<a href="${window.APP_CONFIG.link.pw_reset}">${this.$t('dss.button.passwordReset')}</a>`
                  }));
              }
            } else if (e.response?.data?.code === 'CANNOT_REUSE_PASSWORD') {
              return this.alert(this.$t('dss.page.security.msg.passwordAlreadyUsed'));
            }
            this.alert(this.$t('dss.page.security.msg.pwUpdateError'));
          });

    }
  },
  mounted() {
    console.assert(!!this.email, 'DSS:SPP email missing');
    console.assert(!!this.setPasswordToken, 'DSS:SPP token missing');
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        newPassword: {
          required,
          minLength: minLength(12),
          maxLength: maxLength(127),
          hasLowercase,
          hasUppercase,
          hasDigit,
          hasSpecial: hasSpecial('! $ % & * - + = ; : , . " \' ? ~ # < > | § / @ ^ _ ( ) [ ] { }'),
          invalidCharacters
        },
        newPassword2: {
          required,
          sameAsPassword: sameAs(this.form.newPassword)
        },
        terms: {
          tcAccepted: tcAccepted(this.showTC)
        }
      }
    };
  },
});
</script>

<style scoped>
</style>
